import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
type Tool = {
  key:         string;
  title:       string;
  path:        string;
  author:      string;
  authorLink:  string;
  description: string;
  preview:     string;
};

// ─── Tool registry ────────────────────────────────────────────────────────────
const TOOLS: Tool[] = [
  {
    key:         "checklist",
    title:       "Checklist",
    path:        "/checklist",
    author:      "slingshotswitch",
    authorLink:  "https://linktr.ee/Slingshotswitch",
    description: "Tracks legends, weapons, and legends with weapons",
    preview:     "/tool-previews/checklist.png",
  },
  {
    key:         "weapon-randomizer",
    title:       "Weapon Randomizer",
    path:        "/weapon-randomizer",
    author:      "slingshotswitch",
    authorLink:  "https://linktr.ee/Slingshotswitch",
    description: "Rolls 1 or 3 random weapons",
    preview:     "/tool-previews/weapon-randomizer.png",
  },
  {
    key:         "legend-randomizer",
    title:       "Legend Randomizer",
    path:        "/legend-randomizer",
    author:      "slingshotswitch",
    authorLink:  "https://linktr.ee/Slingshotswitch",
    description: "Rolls a random legend",
    preview:     "/tool-previews/legend-randomizer.png",
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
function ToolsStyles() {
  return (
    <style>{`
      .tools-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 16px;
      }
      .tool-card {
        background: var(--bg-card);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        padding: 14px;
        font-family: var(--font-body);
        color: var(--text);
        transition: background-color 0.15s, border-color 0.15s, transform 0.1s;
      }
      .tool-card:hover {
        background: var(--bg-card-hover);
        border-color: rgba(255,255,255,0.18);
        transform: translateY(-1px);
      }
      .tool-card__clickable {
        cursor: pointer;
        text-align: left;
      }
      .tool-card__preview {
        width: 100%;
        aspect-ratio: 5 / 3;
        border-radius: var(--radius-sm);
        overflow: hidden;
        margin-bottom: 12px;
        background: rgba(0,0,0,0.2);
      }
      .tool-card__preview img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .tool-card__title {
        font-size: 17px;
        font-weight: 700;
      }
      .tool-card__description {
        font-size: 13px;
        color: var(--text-dim);
        margin-top: 4px;
        line-height: 1.4;
      }
      .tool-card__author {
        font-size: 12px;
        color: var(--text-muted);
        margin-top: 8px;
      }
      .tool-card__author-link {
        color: var(--text-dim);
        text-decoration: none;
      }
      .tool-card__author-link:hover {
        font-weight: 700;
        color: var(--text);
      }
    `}</style>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ToolsPage({ navigateTo }: { navigateTo: (path: string) => void }) {
  return (
    <div className="page-inner">
      <ToolsStyles />

      <div className="card roster-page-header">
        <div className="label-accent mb-12">QUIT</div>
        <div className="flex justify-between items-center gap-20 flex-wrap">
          <h1 style={{ margin: 0, fontSize: 46, lineHeight: 1.05 }}>Tools</h1>
        </div>
        <p className="text-muted mb-0 mt-8">Extra tools built by the community.</p>
      </div>

      <div className="tools-grid">
        {TOOLS.map((tool) => (
          <div key={tool.key} className="tool-card">
            <div
              className="tool-card__clickable"
              onClick={() => navigateTo(tool.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigateTo(tool.path); }}
            >
              <div className="tool-card__preview">
                <img src={tool.preview} alt={`${tool.title} preview`} />
              </div>
              <div className="tool-card__title">{tool.title}</div>
              <div className="tool-card__description">{tool.description}</div>
            </div>
            <div className="tool-card__author">
              made by{" "}
              <a
                href={tool.authorLink}
                target="_blank"
                rel="noreferrer"
                className="tool-card__author-link"
                onClick={(e) => e.stopPropagation()}
              >
                {tool.author}
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
